import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useHostProfiles } from "@/hooks/useHostProfiles";
import { sortEventGroups } from "@/lib/eventGroups";
import type { EventGroupAvailability } from "@/types/event";

export const useEventGroupAvailability = (eventIds: string[]) => {
  const ids = Array.from(new Set(eventIds.filter(Boolean))).sort();
  const availability = useQuery({
    queryKey: ["event-group-availability", ids],
    enabled: ids.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase.rpc("get_event_group_availability", { _event_ids: ids });
      if (error) throw error;
      return (data ?? []) as EventGroupAvailability[];
    },
    staleTime: 10_000,
    refetchInterval: ids.length === 1 ? 10_000 : false,
  });

  const hostIds = (availability.data ?? []).flatMap((group) => [group.host_id, group.secondary_host_id]);
  const { data: hostMap, isLoading: hostsLoading } = useHostProfiles(hostIds);
  const groups = (availability.data ?? []).map((group) => ({
    ...group,
    host: group.host_id ? hostMap?.[group.host_id] : undefined,
    secondary_host: group.secondary_host_id ? hostMap?.[group.secondary_host_id] : undefined,
  }));

  const byEvent = new Map<string, EventGroupAvailability[]>();
  groups.forEach((group) => byEvent.set(group.event_id, [...(byEvent.get(group.event_id) ?? []), group]));
  byEvent.forEach((eventGroups, eventId) => byEvent.set(eventId, sortEventGroups(eventGroups)));

  return {
    groups,
    byEvent,
    isLoading: availability.isLoading || hostsLoading,
    isError: availability.isError,
    refetch: availability.refetch,
  };
};